import { PageNotFound } from "@pages/page-not-found";
import {
  Route,
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
} from "react-router-dom";
import {
  MemberProfileTab,
  CompanyProfileTab,
  MainRoutes,
  ProjectProfileTab,
} from "@router/route-params";
import { ProjectProfilePage } from "@pages/project-profile/project-profile-page";
import { CreateProjectPlanPage } from "@pages/create-plan/create-project-plan-page";
import { WorkspaceProfilePage } from "@pages/workspace-profile/workspace-profile-page";
import { CreateWorkspacePlanPage } from "@pages/create-plan/create-workspace-plan-page";
import { CreateProjectForWorkspace } from "@pages/create-project/create-project-for-workspace";
import { MemberProfilePage } from "@pages/member-profile/member-profile-page";
import { HomePage } from "@pages/home/home-page";
import { App } from "@src/app";
import * as Sentry from "@sentry/react";
import { AboutProductsPage } from "@pages/about-products/about-products-page";
import { TestingImpersonationPage } from "@pages/testing-impersonation/testing-impersonation-page";
import { isProductionEnvironment } from "@utils/env-utils";

/**
 * Sentry's React Router integration
 * https://docs.sentry.io/platforms/javascript/guides/react/features/react-router/#react-router-v6
 */
const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);

export const router = sentryCreateBrowserRouter(
  createRoutesFromElements(
    <Route>
      <Route
        path={MainRoutes.root}
        element={<App />}
        errorElement={<PageNotFound />}
      >
        <Route
          path={MainRoutes.aboutProducts}
          element={<AboutProductsPage />}
          errorElement={<PageNotFound />}
        />
        {!isProductionEnvironment && (
          <Route
            path={MainRoutes.testingImpersonation}
            element={<TestingImpersonationPage />}
            errorElement={<PageNotFound />}
          />
        )}
        <Route
          path={MainRoutes.root}
          element={<HomePage />}
          errorElement={<PageNotFound />}
        />
        <Route path={MainRoutes.company}>
          <Route
            path={":companyId"}
            element={
              <Navigate to={CompanyProfileTab.companyInformation} replace />
            }
          />
          <Route
            path={":companyId/:companyProfileTab"}
            element={<WorkspaceProfilePage />}
          />
          <Route
            path={":companyId/add-plan"}
            element={<CreateWorkspacePlanPage creationMode="subscription" />}
          />
          <Route
            path={":companyId/add-feature"}
            element={<CreateWorkspacePlanPage creationMode="feature" />}
          />
          <Route
            path={":companyId/add-device-plan"}
            element={
              <CreateWorkspacePlanPage creationMode="hardware-license" />
            }
          />
          <Route
            path={":companyId/copy-plan/:planId"}
            element={<CreateWorkspacePlanPage creationMode="subscription" />}
          />
          <Route
            path={":companyId/group/:groupId/add-project"}
            element={<CreateProjectForWorkspace />}
          />
        </Route>

        <Route path={MainRoutes.project}>
          <Route
            path={":projectId"}
            element={
              <Navigate to={ProjectProfileTab.projectInformation} replace />
            }
          />
          <Route
            path={":projectId/:projectProfileTab"}
            element={<ProjectProfilePage />}
          />
          <Route
            path={":projectId/add-plan"}
            element={<CreateProjectPlanPage creationMode="subscription" />}
          />
          <Route
            path={":projectId/add-feature"}
            element={<CreateProjectPlanPage creationMode="feature" />}
          />
          <Route
            path={":projectId/copy-plan/:planId"}
            element={<CreateProjectPlanPage creationMode="subscription" />}
          />
        </Route>

        <Route path={MainRoutes.member}>
          <Route
            path={":memberId"}
            element={
              <Navigate to={MemberProfileTab.memberInformation} replace />
            }
          />
          <Route
            path={":memberId/:memberProfileTab"}
            element={<MemberProfilePage />}
          />
        </Route>
      </Route>
    </Route>
  ),
  {
    // Enable future flags that introduces API breaking changes.
    // This allows to opt in to one change a time: https://reactrouter.com/en/main/guides/api-development-strategy
    future: {
      // eslint-disable-next-line @typescript-eslint/naming-convention -- keys coming from external package
      v7_relativeSplatPath: true,
    },
  }
);
